import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { signIn } from "@/lib/session";
import { decodeJwt } from "@/lib/jwt";
import { resolveScope, resolveStaffIds } from "@/lib/authScope";
import { getMerchantClientId } from "@/lib/payerClient";
import { createLocalSession } from "@/hooks/useLocalSession";
import { setAuth } from "@/store/slices/authSlice";
import { safeStorage } from "@/shared/lib/safeStorage";

import { getRedirectHref } from "../lib/redirect";
import { AUTH_STORAGE_KEYS } from "../lib/storageKeys";

import { Alert, AlertDescription } from "@/shared/ui/alert/Alert";
import { Button } from "@/shared/ui/button/Button";
import { AuthCard, AuthShell } from "./AuthShell";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form/Form";
import { Input } from "@/shared/ui/input/Input";

const tokenSchema = z.object({
  token: z.string().trim().min(1, "Please enter a token."),
});

type TokenFormValues = z.infer<typeof tokenSchema>;

interface TokenClaims {
  sub?: string;
  source?: string;
  role?: string;
  staffType?: string;
  staffTypeId?: number;
  isAgent?: boolean;
  merchantId?: string;
  targetDatabase?: string;
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
}

/**
 * Token login. Ports `src/app/login-with-token/page.tsx`: decode the JWT,
 * establish the client session via `signIn("credentials", …)` (unchanged
 * integration), hydrate the auth slice from the token claims, then route to
 * the scope's landing page. If a `?token=` query param is present the login
 * runs once automatically and the input is locked.
 */
export function LoginWithTokenView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const urlLoginAttempted = useRef(false);

  const tokenFromUrl = searchParams.get("token");
  const lockedToUrlToken = !!tokenFromUrl;

  const form = useForm<TokenFormValues>({
    resolver: zodResolver(tokenSchema),
    defaultValues: { token: "" },
  });

  const doLogin = useCallback(
    async (rawToken: string) => {
      const trimmed = rawToken.trim();
      if (!trimmed) {
        setFormError("Please enter a token.");
        return;
      }

      const payload = decodeJwt(trimmed) as TokenClaims | null;
      if (!payload) {
        setFormError("Invalid token. Could not decode JWT.");
        return;
      }

      setIsSubmitting(true);
      setFormError(null);

      try {
        const merchantIdFromToken = payload.merchantId ?? payload.targetDatabase;
        const signed = await signIn("credentials", {
          redirect: false,
          loginToken: trimmed,
          merchantId: merchantIdFromToken,
        });

        if (signed?.error) {
          setFormError("Unable to start session. Please check the token.");
          setIsSubmitting(false);
          return;
        }

        const username = payload.sub ?? "user";
        const local = createLocalSession(username);
        // Dev-parity scope resolution — staffType first (CLIENT → payer,
        // PROVIDER/ADMIN → clinic, …), PGLX platform source as fallback.
        const normalizedSource = resolveScope(payload);

        dispatch(
          setAuth({
            user: {
              username,
              role: payload.role,
              merchantId: merchantIdFromToken,
              source: normalizedSource,
              isAgent: payload.isAgent === true,
              ...resolveStaffIds(payload),
            },
            sessionId: local.sessionId,
          })
        );

        if (normalizedSource === "payer" && merchantIdFromToken) {
          const clientId = getMerchantClientId(merchantIdFromToken);
          if (clientId) {
            safeStorage.setItem(AUTH_STORAGE_KEYS.PAYER_MERCHANT_ID, merchantIdFromToken);
          }
        }

        // Agents and agency admins land in their portal, the only place that is theirs.
        navigate(payload.isAgent === true ? "/agent-portal" : getRedirectHref(normalizedSource), {
          replace: true,
        });
      } catch (err) {
        setFormError((err as Error)?.message ?? "Login failed.");
        setIsSubmitting(false);
      }
    },
    [dispatch, navigate]
  );

  // If a token is present in the URL, run login once on mount.
  useEffect(() => {
    if (tokenFromUrl && !urlLoginAttempted.current) {
      urlLoginAttempted.current = true;
      form.setValue("token", tokenFromUrl);
      void doLogin(tokenFromUrl);
    }
  }, [tokenFromUrl, form, doLogin]);

  const onSubmit = (values: TokenFormValues) => {
    void doLogin(values.token);
  };

  return (
    <AuthShell>
      <AuthCard title="Sign in with token" subtitle="Paste your access token to sign in.">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Access token</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="eyJhbGciOiJIUzI1NiJ9..."
                      className="font-mono text-sm"
                      disabled={lockedToUrlToken}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              isLoading={isSubmitting}
              disabled={isSubmitting || lockedToUrlToken}
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Form>
      </AuthCard>
    </AuthShell>
  );
}
